import { useState, useMemo } from 'react'
import { Table, Skeleton } from 'antd'
import cls from 'classnames'
import { ColumnsType, TableRowSelection } from 'antd/lib/table/interface'
import { FileHistorySourcesEnum, FilePermissionsIdsEnum } from '@medentee/enums'
import { useTranslation } from 'react-i18next'

import { VIDEO_WHITELIST } from 'globalConstants'
import { getExtensionWithoutDot, getSelectedFiles } from 'utils/files'
import { formatShortDate } from 'utils/formats'
import { createSlides, FILE_SHARED_LIST_DEFAULT_PAGINATION, TAccount, TIds } from 'store'
import { formatBytes, stopPropagation } from 'utils'
import { TFileSharedListContainerProps, VideoReadinessStatus } from 'App/containers'
import {
  ContextMenu,
  FileName,
  FileViewToggle,
  PermissionIcons,
  TableTitle,
  TFileViewToggleProps,
  VideoStatusContextWrapper
} from 'App/components'
import { useAdaptiveLayout, useListContextMenu } from 'App/hooks'

import { TFileSharedTableProps } from '../FileSharedList'
import { HeadContextMenu, TableRow, THeadContextMenuProps, TUseSelection } from '../../common'
import { TUseKebabMenu } from '../useKebabMenu'

import styles from './TableView.module.scss'

export type TTableViewProps = Pick<TFileSharedListContainerProps, 'sorting'> &
  Pick<TUseSelection, 'onSelect' | 'selectMode'> &
  TUseKebabMenu &
  Pick<THeadContextMenuProps, 'filesSize'> & {
    dataSource: TFileSharedTableProps[]
    view: TFileViewToggleProps['variant']
    onViewChange: TFileViewToggleProps['onChange']
    onSorting: (key: string) => void
    selectedIds: TIds
    setSelectedIds: (ids: TIds) => void

    loading?: boolean
  }

const SKELETON_ROWS = FILE_SHARED_LIST_DEFAULT_PAGINATION.showBy

export const TableView = ({
  dataSource,
  selectedIds,
  loading,
  view,
  sorting,
  selectMode,
  filesSize,
  setSelectedIds,
  onViewChange,
  onSorting,
  onSelect,
  getMenuItems
}: TTableViewProps) => {
  const [visible, setVisible] = useState<string | null>(null)

  const { isDesktop } = useAdaptiveLayout()

  const { shouldShowContextMenu, shouldShowHeadContextmenu } = useListContextMenu(selectedIds)

  const { t } = useTranslation()

  const selectedFiles = useMemo(
    () => getSelectedFiles(selectedIds, dataSource),
    [selectedIds, dataSource]
  )

  const COLUMNS: ColumnsType<TFileSharedTableProps> = [
    {
      dataIndex: 'fileName',
      key: 'fileName',
      sorter: true,
      className: styles.fileNameWrapper,
      render: (fileName: string, record: TFileSharedTableProps) => (
        <div className={cls(styles.sortable, styles.fileName)}>
          <FileName
            extension={record.extension}
            fileName={fileName}
            fileNameDisabled={record.isUnopenable}
          />
          {isDesktop && (
            <VideoReadinessStatus className={styles.videoStatus} fileId={record.fileId} size="sm" />
          )}
        </div>
      ),
      title: () => (
        <HeadContextMenu
          menuItems={getMenuItems(selectedFiles)}
          visible={shouldShowHeadContextmenu}
          total={selectedIds.length}
          filesSize={filesSize}
        >
          <TableTitle
            title={t('files.columns.name')}
            sortId="fileName"
            sorting={sorting}
            sorter={true}
          />
        </HeadContextMenu>
      ),
      onHeaderCell: (column: any) => ({
        onClick: () => onSorting(column.key)
      })
    },
    {
      dataIndex: 'owner',
      key: 'owner',
      sorter: true,
      className: styles.ownerWrapper,
      title: () => (
        <TableTitle
          title={t('files.columns.owner')}
          sorting={sorting}
          sortId="owner"
          sorter={true}
        />
      ),
      render: (owner: TAccount) => <div className={styles.sortable}>{owner.displayUserName}</div>,
      onHeaderCell: (column: any) => ({
        onClick: () => onSorting(column.key)
      })
    },
    {
      dataIndex: 'permissions',
      key: 'permissions',
      className: styles.permissionWrapper,
      render: (permissions: FilePermissionsIdsEnum[] = []) => (
        <PermissionIcons permissions={permissions} id={'1'} />
      ),
      title: () => <TableTitle title={t('files.columns.permissions')} sortId="permissions" />
    },
    {
      dataIndex: 'sharedAt',
      key: 'sharedAt',
      sorter: true,
      className: styles.sharedAtWrapper,
      render: (sharedAt: string | Date) => (
        <div className={styles.sortable}>{sharedAt && formatShortDate(sharedAt)}</div>
      ),
      title: () => (
        <TableTitle
          title={t('files.columns.sharedOn')}
          sorting={sorting}
          sortId="sharedAt"
          sorter={true}
        />
      ),
      onHeaderCell: (column: any) => ({
        onClick: () => onSorting(column.key)
      })
    },
    {
      dataIndex: 'fileSize',
      key: 'fileSize',
      sorter: true,
      className: styles.fileSizeWrapper,
      render: (fileSize: string) => <div className={styles.sortable}>{formatBytes(fileSize)}</div>,
      title: () => (
        <TableTitle
          title={t('files.columns.fileSize')}
          sorting={sorting}
          sortId="fileSize"
          sorter={true}
        />
      ),
      onHeaderCell: (column: any) => ({
        onClick: () => onSorting(column.key)
      })
    },
    {
      title: <FileViewToggle variant={view} onChange={onViewChange} />,
      dataIndex: 'actions',
      key: 'actions',
      className: styles.kebabMenuWrapper,
      render: (key: string, record: TFileSharedTableProps) =>
        shouldShowContextMenu(record.fileId) && (
          <div onClick={stopPropagation}>
            <ContextMenu
              key={key}
              nestIn="body"
              menuItems={getMenuItems(record)}
              visible={record.fileId === visible}
              endAdornment={
                VIDEO_WHITELIST.includes(getExtensionWithoutDot(record.extension)) &&
                !isDesktop && <VideoStatusContextWrapper fileId={record.fileId} />
              }
              classes={{ icon: styles.contextMenuIcon }}
            />
          </div>
        )
    }
  ]

  const rowSelection: TableRowSelection<TFileSharedTableProps> = {
    selectedRowKeys: selectedIds,
    onChange: (selectedRowIds) => setSelectedIds(selectedRowIds as TIds),
    preserveSelectedRowKeys: true
  }

  return (
    <Skeleton
      loading={loading}
      active={true}
      title={{ width: '100%' }}
      paragraph={{ rows: SKELETON_ROWS }}
    >
      <Table
        columns={COLUMNS}
        dataSource={dataSource}
        loading={loading}
        rowSelection={rowSelection}
        pagination={false}
        showSorterTooltip={false}
        tableLayout="fixed"
        onRow={({ fileId, isUnopenable, slidesEntity }) =>
          TableRow({
            file: { id: fileId },
            openHandlerProps: {
              slides: createSlides(slidesEntity, FileHistorySourcesEnum.MED_CLOUD)
            },
            selectMode,
            disableOpen: isUnopenable,
            onSelect,
            setVisible
          })
        }
      />
    </Skeleton>
  )
}
